<script>
import appConfig from '@/app.config'
import { Helper, debounce } from '@/mixin/helper'
import { required } from 'vuelidate/lib/validators'
import * as pinType from '@/services/rest-api-tools/lib/pin-type/pinType/'
import * as sensorType from '@/services/rest-api-tools/lib/sensor-type/sensorType/'
import * as deviceType from '@/services/rest-api-sms/lib/device-type/device-type/'
import * as alertCode from '@/services/rest-api-sms/lib/alertCode/alertCode/'
import * as checklistApi from '@/services/rest-api-sms/lib/checklist/checklist/'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header'
import Multiselect from 'vue-multiselect'
import draggable from 'vuedraggable'
import moment from 'moment'
import Confirmation from '@/components/widgets/modals/Confirmation'
import LoadingSkeleton from './components/IndexSkeleton.vue'
import { workOrderTypes } from '../lib/workOrderTypes'
import { mapGetters } from 'vuex'

moment.locale('id')

/**
 * Starter component
 */
export default {
  page: {
    title: 'Work Order SOP',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    Multiselect,
    draggable,
    Confirmation,
    LoadingSkeleton,
  },

  mixins: [Helper],

  props: {
    formErrors: {},
  },

  data() {
    return {
      indeterminate: null,
      form: {
        device: null,
        command: null,
        pin: null,
        sensor: null,
      },
      loading: false,
      selected: [],
      selectedModel: [],
      allSelected: false,
      isDeleting: false,
      loadingTable: false,
      checklists: [],
      beforeDragChecklists: [],
      checklist: {
        woTypes: [],
        deviceTypes: [],
        pinTypes: [],
        sensorTypes: [],
        interval: null,
        isActive: false,
        isMandatory: false,
        name: null,
        flag: null,
        relatedChecklists: [],
        sop_reference: null,
        is_validation_required: false,
      },
      dropdown: {
        woTypes: [],
        deviceTypes: [],
        sensorTypes: [],
        pinTypes: [],
        alertCodes: [],
        relatedChecklists: [],
      },
      modalConfirm: false,
      filter: null,
      filterWoType: null,
      fileAccepted: ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'],
      intervalOptions: [
        { value: '00:01:00', text: '1 minute' },
        { value: '00:05:00', text: '5 minute' },
        { value: '00:10:00', text: '10 minute' },
        { value: '00:15:00', text: '15 minute' },
        { value: '00:20:00', text: '20 minute' },
        { value: '00:30:00', text: '30 minute' },
        { value: '01:00:00', text: '1 hour' },
      ],
      previewImgSrc: null,
    }
  },

  validations: {
    checklist: {
      woTypes: { required },
      name: { required },
      isMandatory: { required },
      flag: { required },
      deviceTypes: { required },
    },
  },

  computed: {
    ...mapGetters({
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    title() {
      return this.$t('workOrder.column.sop')
    },

    subTitle() {
      return this.$t('workOrder.subColumn.sop')
    },

    isSopReferenceActive() {
      return this.getFeatureFlagData('sop_reference_active').value
    },

    woTypeOptions() {
      const dataWorkOrderTypes = workOrderTypes()
      const selectedCodes = new Set(this.checklist.woTypes?.map((type) => type.code))
      const woTypes = dataWorkOrderTypes.filter((option) => !selectedCodes.has(option.code))
      return woTypes
    },

    deviceTypeOptions() {
      const deviceTypes = this.dropdown.deviceTypes

      // Create the "All" option
      const allOption = { deviceCode: null, deviceName: 'All' }

      // If "All" is already selected, only return the "All" option
      if (
        this.checklist.deviceTypes &&
        this.checklist.deviceTypes.some((type) => type.deviceCode === null)
      ) {
        return [allOption]
      }

      // Otherwise, filter out already selected options and add "All" at the beginning
      const selectedCodes = new Set(this.checklist.deviceTypes?.map((type) => type.deviceCode))
      const dt = deviceTypes.filter((option) => !selectedCodes.has(option.deviceCode))

      return [allOption, ...dt]
    },

    sensorTypeOptions() {
      const sensorTypes = this.dropdown.sensorTypes
      const selectedCodes = new Set(this.checklist.sensorTypes?.map((type) => type.sensor_code))
      const st = sensorTypes.filter((option) => !selectedCodes.has(option.sensor_code))
      return st
    },

    pinTypeOptions() {
      const pinTypes = this.dropdown.pinTypes
      const selectedCodes = new Set(this.checklist.pinTypes?.map((type) => type.pin_code))
      const pt = pinTypes.filter((option) => !selectedCodes.has(option.pin_code))
      return pt
    },

    alertCodeOptions() {
      const alertCodes = this.dropdown.alertCodes
      const selectedCodes = new Set(this.checklist.alertCodes?.map((type) => type.alert_code))
      const ac = alertCodes.filter((option) => !selectedCodes.has(option.alert_code))
      return ac
    },

    relatedChecklistOptions() {
      const relatedChecklistIds = this.dropdown.relatedChecklists
      const selectedRelatedChecklistIds = new Set(
        this.checklist.relatedChecklists?.map((checklist) => checklist.name)
      )
      const rci = relatedChecklistIds.filter(
        (option) => !selectedRelatedChecklistIds.has(option.checklist_id)
      )
      return rci
    },

    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },

  watch: {
    selected(newValue) {
      // Handle changes in individual flavour checkboxes
      if (newValue.length === 0) {
        this.indeterminate = false
        this.allSelected = false
      } else if (newValue.length === this.checklists.length) {
        this.indeterminate = false
        this.allSelected = true
      } else {
        this.indeterminate = true
        this.allSelected = false
      }
    },

    filterWoType() {
      this.getChecklists()
    },
  },

  async created() {
    this.loading = true
    await this.getDeviceTypes()
    await this.getSensorTypes()
    await this.getPinTypes()
    await this.getAlertCodes()
    await this.getRelatedChecklists()
    await this.getChecklists()
    this.loading = false
  },

  methods: {
    async handleSopFileChange(event) {
      const files = event?.target?.files
      const file = files && files[0] ? files[0] : null
      if (!file) return

      if (!this.fileAccepted.includes(file.type)) {
        this.$mcToast.error('Unggah gagal. Format file tidak sesuai.')
        event.target.value = null
        return
      }

      const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB
      if (file.size > MAX_FILE_SIZE) {
        this.$mcToast.error('Unggah gagal. Ukuran file melebihi batas ketentuan.')
        event.target.value = null
        return
      }

      const toBase64 = (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(f)
          reader.onload = () => resolve(reader.result)
          reader.onerror = (error) => reject(error)
        })

      const convertedBase64 = await toBase64(file)
      const base64 = String(convertedBase64)
      const sizeMB = (file.size / 1024 / 1024).toFixed(2)

      this.checklist = {
        ...this.checklist,
        sop_reference: { name: file.name, base64, sizeMB },
      }
    },
    // Add this method to handle selection changes
    handleDeviceTypeSelection(selectedTypes) {
      if (!selectedTypes || selectedTypes.length === 0) {
        this.checklist.deviceTypes = []
        return
      }

      // Check if "All" was just selected
      const allOptionSelected = selectedTypes.some((type) => type.deviceCode === null)
      const hadAllOptionBefore = this.checklist.deviceTypes.some((type) => type.deviceCode === null)

      if (allOptionSelected && !hadAllOptionBefore) {
        // If "All" was just selected, set it as the only selection
        this.checklist.deviceTypes = [{ deviceCode: null, deviceName: 'All' }]
      } else if (!allOptionSelected && hadAllOptionBefore) {
        // If "All" was just deselected, clear the selection
        this.checklist.deviceTypes = []
      } else if (allOptionSelected) {
        // If "All" is still selected, keep only "All"
        this.checklist.deviceTypes = [{ deviceCode: null, deviceName: 'All' }]
      } else {
        // Otherwise, update with the new selection (excluding "All")
        this.checklist.deviceTypes = selectedTypes.filter((type) => type.deviceCode !== null)
      }
    },

    searchData: debounce(function () {
      const params = {
        q: this.filter,
      }
      this.getChecklists(params)
    }, 600),

    async getChecklists(params = {}) {
      this.loadingTable = true
      if (this.filterWoType) {
        params.workOrderType = this.filterWoType.code
      }

      const response = await checklistApi.get(params)

      if (response === null) {
        this.checklists = null
        this.loadingTable = false
        return response
      }

      this.checklists = response

      // If response is an array of checklists
      if (Array.isArray(this.checklists)) {
        // Process each checklist in the array
        this.checklists.forEach((checklist) => {
          if (!checklist.device_type || checklist.device_type === null) {
            checklist.device_type = [{ deviceCode: null, deviceName: 'All' }]
          }
        })
      } else {
        // Handle single checklist object
        if (!this.checklists.device_type || this.checklists.device_type === null) {
          this.checklists.device_type = [{ deviceCode: null, deviceName: 'All' }]
        }
      }

      this.beforeDragChecklists = JSON.parse(JSON.stringify(this.checklists)) // Create a deep copy
      this.loadingTable = false
      return response
    },

    async getRelatedChecklists() {
      const response = await checklistApi.get()
      this.dropdown.relatedChecklists = response
    },

    async getSensorTypes() {
      const sensorTypes = await sensorType.get()
      this.dropdown.sensorTypes = sensorTypes
    },

    async getPinTypes() {
      const pinTypes = await pinType.get()
      this.dropdown.pinTypes = pinTypes
    },

    async getAlertCodes() {
      let queryParam = {
        limit: 1,
        page: 1,
      }
      let alertCodes = await alertCode.get(queryParam)
      queryParam.limit = alertCodes.metadata.total_count
      alertCodes = await alertCode.get(queryParam)
      this.dropdown.alertCodes = alertCodes.data.paginated_result
    },

    async getDeviceTypes() {
      let queryParam = {
        limit: 1,
        page: 1,
      }
      let deviceTypes = await deviceType.get(queryParam)
      queryParam.limit = deviceTypes.metadata.total_count
      deviceTypes = await deviceType.get(queryParam)
      this.dropdown.deviceTypes = deviceTypes.data.paginated_result
    },

    showSopReference(item) {
      const base64 = item?.sop_reference?.base64 || item?.sop_reference || null
      if (!base64) {
        this.$mcToast?.error && this.$mcToast.error('No SOP reference available')
        return
      }
      this.previewImgSrc = base64
      this.$bvModal.show('modal-sop-preview')
    },

    handleOk(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault()
      // Trigger submit handler
      this.handleSubmit()
    },

    handleSubmit() {
      // Hide the modal manually
      this.$nextTick(() => {
        this.$bvModal.hide('modal-create')
        this.resetModal()
      })
    },

    addChecklist() {
      this.checklist = {
        woTypes: [],
        deviceTypes: [],
        pinTypes: [],
        sensorTypes: [],
        interval: null,
        isActive: false,
        isMandatory: false,
        name: null,
        flag: null,
        sop_reference: null,
        is_validation_required: false,
      }
      this.$bvModal.show('modal-sop')
    },

    onRowClicked(item) {
      this.$router.push({ name: 'Work Order SOP Detail', params: { id: item.checklist_id } })
    },

    handleBack() {
      this.$router.back()
    },

    checkTable(event, item) {
      if (event) {
        this.selected.push(item.checklist_id)
        this.selectedModel[item.checklist_id] = true
      } else {
        let myIndex = this.selected.indexOf(item.checklist_id)
        if (myIndex !== -1) {
          this.selected.splice(myIndex, 1)
        }
        this.selectedModel[item.checklist_id] = false
      }
    },

    toggleAll(checked) {
      this.selected = []
      this.selectedModel = {}

      if (checked) {
        this.checklists.forEach((item) => {
          this.selected.push(item.checklist_id)
          this.selectedModel[item.checklist_id] = true
        })
      } else {
        this.selectedModel = {}
      }
    },

    async toDelete() {
      this.isDeleting = true
      this.loadingTable = true

      const payload = {
        ids: this.selected,
      }

      const response = await checklistApi.remove(payload)
      if (!response?.data || response.status !== 200) {
        this.isDeleting = false
        this.loadingTable = false
        return
      }

      await this.getChecklists()

      this.$mcToast.success(`Data successfully ${response.data}`)
      this.clearDelete()

      this.isDeleting = false
      this.loadingTable = false
    },

    clearDelete() {
      this.allSelected = false
      this.selectedModel = []
      this.selected = []
    },

    confirmDelete() {
      this.modalConfirm = true
    },

    getWorkOrderTypeLabel(code) {
      const dataWorkOrderTypes = workOrderTypes()
      const type = dataWorkOrderTypes.find((item) => item.code === code)
      return type ? type.name : code
    },

    normalizeIntervalValue(interval) {
      if (!interval) return null
      if (typeof interval === 'number' || /^[0-9]+$/.test(String(interval))) {
        const minutes = Number(interval)
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`
      }
      return interval
    },

    getIntervalLabel(interval) {
      const normalizedInterval = this.normalizeIntervalValue(interval)
      const option = this.intervalOptions.find((item) => item.value === normalizedInterval)
      return option ? option.text : normalizedInterval || '-'
    },

    async handleDragEnd({ oldIndex, newIndex }) {
      const originChecklist = this.beforeDragChecklists[oldIndex]
      const destChecklist = this.beforeDragChecklists[newIndex]

      const payload = {
        checklist_id: originChecklist.checklist_id,
        sorting: destChecklist.sorting,
      }

      const response = await checklistApi.updateSorting(payload)
      if (response.status === 200) {
        this.$mcToast.success('Successfully updated sop order')
      }
      await this.getChecklists()
    },

    handleFailedValidation() {
      return
    },

    async createChecklist() {
      this.loadingTable = true
      this.$v.checklist.$touch()
      if (this.$v.checklist.$error) {
        this.loadingTable = false
        return
      }

      const rawDeviceTypes = this.checklist.deviceTypes

      let deviceCodes = null

      if (Array.isArray(rawDeviceTypes)) {
        const filtered = rawDeviceTypes
          .map((t) => t?.deviceCode)
          .filter((code) => Number.isInteger(code))

        deviceCodes = filtered.length > 0 ? filtered : null
      }

      const now = moment(new Date()).utc().format('YYYY-MM-DD HH:mm:ss')
      const payload = {
        name: this.checklist.name,
        flag: this.checklist.flag,
        wo_types: this.checklist.woTypes?.map((item) => item.code) || null,
        device_codes: deviceCodes,
        sensor_codes: this.checklist.sensorTypes?.map((item) => item.sensor_code) || null,
        alert_codes: this.checklist.alertCodes?.map((item) => item.alert_code) || null,
        pin_codes: this.checklist.pinTypes?.map((item) => item.pin_code) || null,
        related_checklist_ids:
          this.checklist.relatedChecklists?.map((item) => item.checklist_id) || null,
        c_interval: this.checklist.interval,
        is_active: this.checklist.isActive,
        is_mandatory: this.checklist.isMandatory,
        created_at: now,
        updated_at: now,
        sorting: this.checklists.length + 1,
        sop_reference: this.checklist.sop_reference?.base64 || null,
        is_validation_required: this.checklist.is_validation_required,
      }
      const data = await checklistApi.store(payload)
      if (data.status === 201) {
        this.getChecklists()
        this.$mcToast.success('Successfully created sop')
        this.$bvModal.hide('modal-sop')
      } else {
        this.$mcToast.error(`Failed to create sop: ${data.data.errors}`)
      }
      this.loadingTable = false
    },
  },
}
</script>

<style>
.formulate-input ul.formulate-input-errors {
  font-style: normal;
  font-size: 80%;
}
.tes {
  text-align: middle;
  text-align: center;
}
</style>

<template>
  <Layout>
    <PageHeader :title="title" />
    <p class="mb-4">Mengelola SOP untuk teknisi</p>
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <LoadingSkeleton />
      </template>
      <div class="clearfix mc-btn-group__list">
        <div class="float-end">
          <b-button
            @click="handleBack"
            variant="outline-primary"
            menu-class="dropdown-menu-end"
            class="ms-3"
          >
            <i class="bx bx-arrow-back"></i> {{ $t('global.back') }}
          </b-button>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-6"></div>

        <div class="col-md-6 d-flex justify-content-end gap-2">
          <div class="mc-table-filter">
            <a
              v-if="hasAccessAll(['sms-sop:create'])"
              class="popup-form btn btn-primary"
              href="javascript: void(0);"
              @click="addChecklist"
            >
              <i class="bx bx-plus"></i> {{ $t('global.addField') }}
            </a>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="clearfix">
                <div class="float-start">
                  <multiselect
                    v-model="filterWoType"
                    :options="woTypeOptions"
                    label="name"
                    track-by="code"
                    placeholder="Filter by work order type"
                    style="min-width: 202px"
                  />
                  <h6
                    v-if="selected.length && hasAccessAll(['sms-sop:delete'])"
                    class="mc-row-check"
                  >
                    <span class="badge btn-success">{{ selected.length }}</span>
                    Baris terpilih.
                    <a class="color-primary" @click="clearDelete">Bersihkan semua</a>
                  </h6>
                </div>

                <!-- Search -->
                <div class="float-end">
                  <div class="mc-table-view">
                    <div
                      id="tickets-table_filter"
                      class="dataTables_filter text-md-end d-flex align-items-center"
                    >
                      <div class="text-end mb-2">
                        <b-button
                          v-if="selected.length && hasAccessAll(['sms-sop:delete'])"
                          :disabled="isDeleting"
                          @click="confirmDelete"
                          variant="outline-danger"
                          class="mx-2"
                        >
                          <i class="bx bx-trash-alt"></i> {{ $t('global.delete') }}
                        </b-button>
                      </div>
                      <label class="d-inline-flex align-items-center mc-table-view__search ms-2">
                        <i class="mc-fa fas fa-search"></i>
                        <b-form-input
                          v-model="filter"
                          type="search"
                          :placeholder="$t('global.search') + '...'"
                          class="ms-2 form-control"
                          @input="searchData"
                        />
                      </label>
                    </div>
                  </div>
                </div>
                <!-- End search -->
              </div>

              <div class="table-responsive my-3 border border-1 table-container">
                <b-overlay :show="loadingTable" rounded="sm">
                  <table class="table">
                    <thead>
                      <tr>
                        <th>
                          <div class="form-check">
                            <b-form-checkbox
                              v-model="allSelected"
                              :indeterminate="indeterminate"
                              @change="toggleAll"
                            />
                          </div>
                        </th>
                        <th v-if="hasAccessAll(['sms-sop:update'])"></th>
                        <!-- For drag handle -->
                        <th>{{ $t('workOrder.sop.checklistName') }}</th>
                        <th>{{ $t('workOrder.sop.relatedChecklists') }}</th>
                        <th>{{ $t('workOrder.sop.woType') }}</th>
                        <th>{{ $t('workOrder.sop.checklistFlag') }}</th>
                        <th>{{ $t('workOrder.sop.deviceType') }}</th>
                        <th>{{ $t('workOrder.sop.sensorType') }}</th>
                        <th>{{ $t('workOrder.sop.pinType') }}</th>
                        <th>{{ $t('workOrder.sop.alertCode') }}</th>
                        <th>{{ $t('workOrder.sop.interval') }}</th>
                        <th>{{ $t('workOrder.sop.isMandatory') }}</th>
                        <th>{{ $t('workOrder.sop.isValidationRequired') }}</th>
                        <th>Need Engine</th>
                        <th>Need All Pass</th>
                        <th>{{ $t('workOrder.sop.isActive') }}</th>
                        <th v-if="isSopReferenceActive">{{ $t('workOrder.sop.sopReference') }}</th>
                      </tr>
                    </thead>
                    <tbody v-if="!checklists || checklists.length === 0">
                      <tr>
                        <td colspan="14" class="text-center py-4">
                          <div class="empty-state">
                            <i class="bx bx-folder-open fs-1 text-muted"></i>
                            <p class="mt-2">No data available</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                    <draggable
                      v-model="checklists"
                      tag="tbody"
                      handle=".drag-handle"
                      @end="handleDragEnd"
                    >
                      <tr v-for="item in checklists" :key="item.checklist_id">
                        <td>
                          <div class="form-check">
                            <b-form-checkbox
                              v-model="selectedModel[item.checklist_id]"
                              :true-value="true"
                              :false-value="false"
                              @change="checkTable($event, item)"
                            />
                          </div>
                        </td>
                        <td v-if="hasAccessAll(['sms-sop:update'])">
                          <i class="fas fa-grip-vertical drag-handle cursor-move"></i>
                        </td>
                        <td>
                          <a @click="onRowClicked(item)">
                            <strong class="text-primary">{{ item.name }}</strong>
                          </a>
                        </td>
                        <td>
                          <div class="wo-type-tags">
                            <b-badge
                              v-for="tag in item.related_checklists"
                              :key="tag.checklist_id"
                              class="bg-primary text-white ms-2"
                            >
                              {{ tag.name }}
                            </b-badge>
                          </div>
                        </td>
                        <td>
                          <div class="wo-type-tags">
                            <b-badge
                              v-for="tag in item.wo_type"
                              :key="tag"
                              class="bg-primary text-white ms-2"
                            >
                              {{ getWorkOrderTypeLabel(tag) }}
                            </b-badge>
                          </div>
                        </td>
                        <td>
                          {{ item.flag }}
                        </td>
                        <td>
                          <div class="device-codes-tags">
                            <b-badge
                              v-for="tag in item.device_type"
                              :key="tag.deviceCode"
                              class="bg-primary text-white ms-2"
                            >
                              {{ tag.deviceName }}
                            </b-badge>
                          </div>
                        </td>
                        <td>
                          <div class="sensor-codes-tags">
                            <b-badge
                              v-for="tag in item.sensor_type"
                              :key="tag.sensor_code"
                              class="bg-primary text-white ms-2"
                            >
                              {{ tag.sensor_name }}
                            </b-badge>
                          </div>
                        </td>
                        <td>
                          <div class="pin-codes-tags">
                            <b-badge
                              v-for="tag in item.pin_type"
                              :key="tag.pin_code"
                              class="bg-primary text-white ms-2"
                            >
                              {{ tag.pin_name }}
                            </b-badge>
                          </div>
                        </td>
                        <td>
                          <div class="alert-codes-tags">
                            <b-badge
                              v-for="tag in item.alert_code"
                              :key="tag.alert_code"
                              class="bg-primary text-white ms-2"
                            >
                              {{ tag.description_idn }}
                            </b-badge>
                          </div>
                        </td>
                        <td>{{ getIntervalLabel(item.c_interval) }}</td>
                        <td>
                          <input type="checkbox" v-model="item.is_mandatory" :disabled="true" />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            v-model="item.is_validation_required"
                            :disabled="true"
                          />
                        </td>
                        <td>
                          <input type="checkbox" v-model="item.is_need_engine" :disabled="true" />
                        </td>
                        <td>
                          <input type="checkbox" v-model="item.is_need_all_pass" :disabled="true" />
                        </td>
                        <td>
                          <input type="checkbox" v-model="item.is_active" :disabled="true" />
                        </td>
                        <td v-if="isSopReferenceActive">
                          <b-button
                            size="sm"
                            :variant="`${
                              item.sop_reference ? 'outline-primary' : 'outline-light'
                            } small`"
                            :disabled="!item.sop_reference"
                            @click="showSopReference(item)"
                          >
                            View
                          </b-button>
                        </td>
                      </tr>
                    </draggable>
                  </table>
                </b-overlay>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-skeleton-wrapper>

    <b-modal
      id="modal-sop"
      centered
      :title="`${$t('workOrder.column.sop')} : ${checklist?.name || '-'}`"
      hideHeaderClose
      :okTitle="$t('global.save')"
      okVariant="primary"
      @submit="createChecklist"
      :errors="formErrors"
      @failed-validation="handleFailedValidation"
    >
      <FormulateForm ref="form" v-model="checklist" class="mc-form">
        <FormulateInput
          id="name"
          name="name"
          type="text"
          :validation="['required']"
          :placeholder="$t('workOrder.sop.checklistName')"
          :validation-messages="{
            required: `${$t('workOrder.sop.checklistName')} ${$t('global.validation.required')}`,
          }"
          class="mb-3"
          validation-behavior="live"
          error-behavior="live"
        >
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <label class="teal-300">
                {{ $t('workOrder.sop.checklistName') }}<i class="text-danger">*</i>
              </label>
            </div>
          </template>
        </FormulateInput>

        <div class="mb-3">
          <label>{{ $t('workOrder.sop.relatedChecklists') }}</label>
          <multiselect
            v-model="checklist.relatedChecklists"
            :options="relatedChecklistOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :hide-selected="true"
            :allow-empty="true"
            placeholder="Select related SOP"
            label="name"
            track-by="checklist_id"
          />
        </div>

        <div class="mb-3">
          <label class="required">{{ $t('workOrder.sop.woType') }}</label>
          <multiselect
            v-model="checklist.woTypes"
            :options="woTypeOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :hide-selected="true"
            :allow-empty="true"
            placeholder="Select work order types"
            label="name"
            track-by="code"
            :class="{
              'is-invalid': $v.checklist.woTypes.$error,
            }"
          />
          <div v-if="$v.checklist.woTypes.$error" class="invalid-feedback mt-0">
            <span v-if="!$v.checklist.woTypes.required">
              {{ $t('workOrder.sop.woType') }} Wajib Terisi
            </span>
          </div>
        </div>

        <FormulateInput
          id="flag"
          name="flag"
          type="text"
          validation="required:trim"
          :placeholder="$t('workOrder.sop.checklistFlag')"
          :validation-messages="{
            required: `${$t('workOrder.sop.checklistFlag')} ${$t('global.validation.required')}`,
          }"
          class="mb-3"
          validation-behavior="live"
          error-behavior="live"
        >
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <label class="teal-300">
                {{ $t('workOrder.sop.checklistFlag') }}<i class="text-danger">*</i>
              </label>
            </div>
          </template>
        </FormulateInput>

        <div class="mb-3">
          <label class="required">{{ $t('workOrder.sop.deviceType') }}</label>
          <multiselect
            v-model="checklist.deviceTypes"
            @input="handleDeviceTypeSelection"
            :options="deviceTypeOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :hide-selected="true"
            :allow-empty="true"
            placeholder="Select device types"
            label="deviceName"
            track-by="deviceCode"
            :class="{
              'is-invalid': $v.checklist.deviceTypes.$error,
            }"
          />
          <div v-if="$v.checklist.deviceTypes.$error" class="invalid-feedback mt-0">
            <span v-if="!$v.checklist.deviceTypes.required">
              {{ $t('workOrder.sop.deviceType') }} Wajib Terisi
            </span>
          </div>
        </div>

        <div class="mb-3">
          <label>{{ $t('workOrder.sop.sensorType') }}</label>
          <multiselect
            v-model="checklist.sensorTypes"
            :options="sensorTypeOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :hide-selected="true"
            :allow-empty="true"
            placeholder="Select sensor type"
            label="sensor_name"
            track-by="sensor_code"
          />
        </div>

        <div class="mb-3">
          <label>{{ $t('workOrder.sop.pinType') }}</label>
          <multiselect
            v-model="checklist.pinTypes"
            :options="pinTypeOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :hide-selected="true"
            :allow-empty="true"
            placeholder="Select pin types"
            label="pin_name"
            track-by="pin_code"
          />
        </div>

        <div class="mb-3">
          <label>{{ $t('workOrder.sop.alertCode') }}</label>
          <multiselect
            v-model="checklist.alertCodes"
            :options="alertCodeOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :hide-selected="true"
            :allow-empty="true"
            placeholder="Select alert codes"
            label="description_idn"
            track-by="alert_code"
          />
        </div>

        <div class="mb-3">
          <label>{{ $t('workOrder.sop.interval') }}</label>
          <v-select
            v-model="checklist.interval"
            :options="intervalOptions"
            label="text"
            :reduce="(option) => option.value"
            placeholder="Select interval"
          />
        </div>

        <div class="mb-3">
          <div class="form-check-right form-switch">
            <input
              id="SwitchCheckSizemd"
              v-model="checklist.isMandatory"
              class="form-check-input ms-2"
              type="checkbox"
            />
            <label for="SwitchCheckSizemd">
              {{ $t('workOrder.sop.isMandatory') }}
            </label>
          </div>
        </div>

        <div class="mb-3">
          <div class="form-check-right form-switch">
            <input
              id="SwitchCheckSizemd"
              v-model="checklist.isActive"
              class="form-check-input ms-2"
              type="checkbox"
            />
            <label class="" for="SwitchCheckSizemd">
              {{ $t('workOrder.sop.isActive') }}
            </label>
          </div>
        </div>

        <div class="mb-3">
          <div class="form-check-right form-switch">
            <input
              id="SwitchCreateValidation"
              v-model="checklist.is_validation_required"
              class="form-check-input ms-2"
              type="checkbox"
            />
            <label for="SwitchCreateValidation">
              {{ $t('workOrder.sop.isValidationRequired') || 'Validation Required' }}
            </label>
          </div>
        </div>

        <div v-if="isSopReferenceActive" class="mb-3">
          <label>Sop Reference</label>
          <div class="d-flex align-items-center gap-2">
            <b-button variant="outline-primary" @click="$refs.sopFileInput.click()">
              <i class="icon-fill_plus" />
            </b-button>
            <span>{{
              checklist.sop_reference ? checklist.sop_reference.name : 'No file selected'
            }}</span>
            <input ref="sopFileInput" type="file" class="d-none" @change="handleSopFileChange" />
          </div>
        </div>
      </FormulateForm>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer">
          <b-col sm="12" md="12" class="mc-modal__filter-footer-fav"> </b-col>
          <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
            <b-button
              :disabled="loading || loadingTable"
              variant="outline-primary"
              @click="cancel"
              class="me-3"
            >
              Batal
            </b-button>
            <b-button
              v-if="hasAccessAll(['sms-sop:create'])"
              :disabled="loading || loadingTable"
              type="submit"
              variant="primary"
              @click="createChecklist"
            >
              <b-spinner v-if="loading" small />
              Simpan
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>

    <b-modal
      id="modal-sop-preview"
      centered
      title="SOP Reference"
      hideHeaderClose
      okOnly
      okTitle="Close"
    >
      <div class="w-100 text-center">
        <img v-if="previewImgSrc" :src="previewImgSrc" alt="SOP Reference" class="img-fluid" />
        <div v-else class="text-muted">No preview available</div>
      </div>
    </b-modal>

    <confirmation v-if="modalConfirm" :modal.sync="modalConfirm" @confirmed="toDelete" />
  </Layout>
</template>
